'use client';

import { useState } from 'react';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { useAuthNavigation } from '@eliadmin/core/src/components/dlz/auth/dialog/auth-navigation';
import { ActionButton } from '@eliadmin/core/src/components/dlz/auth/password-reset/action-link';
import { FormAlert } from '@eliadmin/core/src/components/dlz/auth/password-reset/form-alert';
import { RESET_COPY } from '@eliadmin/core/src/components/dlz/auth/password-reset/password-reset-copy';
import { StepHeading } from '@eliadmin/core/src/components/dlz/auth/password-reset/step-heading';
import { formatCountdown } from '@eliadmin/core/src/components/dlz/auth/password-reset/use-resend-cooldown';
import { useSendResetLink } from '@eliadmin/core/src/components/dlz/auth/password-reset/use-send-reset-link';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/** Step 2 – „Skontrolujte si e-mail“ (Figma 60155:20277, mobile 60438:82036). */
export function SentStep({
  email,
  secondsLeft,
  onResent,
  onChangeEmail,
}: {
  email: string;
  secondsLeft: number;
  onResent: () => void;
  onChangeEmail: () => void;
}) {
  const { t } = useTranslations();
  const { showLogin } = useAuthNavigation();
  const sendLink = useSendResetLink();
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);

  const resend = async () => {
    setSending(true);
    setAlert(null);
    try {
      await sendLink(email);
      onResent();
    } catch (error) {
      const messages = error instanceof ValidationError ? Object.values(error.fields).flat() : [];
      setAlert(messages.join('\n') || t('text651'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <StepHeading
        icon="at"
        iconSize={32}
        title={RESET_COPY.sent.title}
        description={RESET_COPY.sent.text(email)}
        focusTitle
      />
      <div className="flex w-full items-start gap-3 rounded-xl bg-[#F5F6F7] p-4">
        <Icon name="lightbulb" size={20} className="text-[#9096A0]" />
        <p className="dlz-body-s text-left text-[#4F545C]">{RESET_COPY.sent.tip}</p>
      </div>
      <div className="flex w-full flex-col items-center gap-3">
        <Button
          type="button"
          onClick={resend}
          isLoading={sending}
          disabled={!email || secondsLeft > 0}
          className="h-12 w-full md:h-[52px] md:w-auto"
        >
          {RESET_COPY.sent.resend}
        </Button>
        <p className="dlz-body-s min-h-5 text-center text-[#6B7079]">
          {secondsLeft > 0 ? RESET_COPY.sent.countdown(formatCountdown(secondsLeft)) : null}
        </p>
        <FormAlert>{alert}</FormAlert>
      </div>
      <div className="flex flex-col items-center gap-4 md:flex-row md:gap-6">
        <ActionButton onClick={onChangeEmail}>{RESET_COPY.sent.otherEmail}</ActionButton>
        <ActionButton onClick={showLogin}>{RESET_COPY.email.back}</ActionButton>
      </div>
    </>
  );
}
